"use client";

import { useEffect, useRef } from "react";
import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";

interface AdvancedIntervalOptions {
  callback: () => void;
  delay: number;
  immediate?: boolean;
  executeOnWindowFocus?: boolean;
  maxExecutions?: number;
  condition: boolean;
}

export const useAdvancedInterval = (options: AdvancedIntervalOptions) => {
  const {
    callback,
    delay,
    immediate = false,
    executeOnWindowFocus = false,
    maxExecutions = Infinity,
    condition,
  } = options;

  const savedCallback = useRef(callback);
  const executionCount = useRef(0);

  // Remember the latest callback if it changes.
  useIsomorphicLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Handle execution count
  const executeCallback = () => {
    if (executionCount.current < maxExecutions && condition) {
      savedCallback.current();
      executionCount.current += 1;
    }
  };

  // Window focus event handler
  useEffect(() => {
    if (!executeOnWindowFocus || !condition) {
      return;
    }

    const callback = () => {
      executeCallback();
    };

    window.addEventListener("focus", callback);
    return () => {
      window.removeEventListener("focus", callback);
    };
  }, [executeOnWindowFocus, condition]);

  // Set up the interval
  useEffect(() => {
    // Reset execution count when dependencies change
    executionCount.current = 0;

    // Don't schedule if condition is false
    if (!condition) {
      return;
    }

    // Call immediately if the immediate option is true
    if (immediate) {
      executeCallback();
    }

    const id = setInterval(() => {
      executeCallback();
    }, delay);

    return () => {
      clearInterval(id);
    };
  }, [delay, immediate, maxExecutions, condition]);

  // Return control functions
  return {
    executionCount: executionCount.current,
    reset: () => {
      executionCount.current = 0;
    },
    execute: executeCallback,
    stop: () => {
      executionCount.current = maxExecutions;
    },
  };
};
